import api from "./api";
import API_ROUTES from "./apiRoutes";
import { toast } from "react-toastify";

/* ---------- COMMON ERROR HANDLER ---------- */
const handleError = (err, label) => {
  console.error(label, err);

  const message =
    err?.data?.error ||
    err?.data?.message ||
    err.message ||
    "Something went wrong";

  toast.error(message);
  // Preserve ApiError details for callers when available.
  throw err;
};

/* ---------- SUCCESS HANDLER ---------- */
const handleSuccess = (message) => {
  toast.success(message);
};

/* =====================================================
   SOCIETY
===================================================== */

export const getSocieties = async (search = "") => {
  try {
    const res = await api.get(API_ROUTES.societies.list, {
      params: search ? { search } : undefined,
    });
    return res.data;
  } catch (err) {
    handleError(err, "GET SOCIETIES ERROR");
  }
};

export const createSociety = async (data) => {
  try {
    const res = await api.post(API_ROUTES.societies.create, data);
    handleSuccess("Society created successfully");
    return res.data;
  } catch (err) {
    handleError(err, "CREATE SOCIETY ERROR");
  }
};

export const uploadSocietyIcon = async (societyId, file) => {
  try {
    const form = new FormData();
    form.append("icon", file);
    const res = await api.post(API_ROUTES.societies.icon(societyId), form);
    handleSuccess("Property icon uploaded");
    return res.data;
  } catch (err) {
    handleError(err, "UPLOAD SOCIETY ICON ERROR");
  }
};

export const getSocietyDocuments = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.documents(societyId));
    return res.data;
  } catch (err) {
    handleError(err, "GET DOCUMENTS ERROR");
  }
};

export const uploadSocietyDocuments = async (societyId, files, metadata) => {
  try {
    const form = new FormData();
    files.forEach((file) => form.append("documents", file));
    form.append("metadata", JSON.stringify(metadata));
    
    const res = await api.post(API_ROUTES.societies.documents(societyId), form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    handleSuccess("Documents uploaded successfully");
    return res.data;
  } catch (err) {
    handleError(err, "UPLOAD DOCUMENTS ERROR");
  }
};

export const deleteSocietyDocument = async (societyId, docId) => {
  try {
    const res = await api.delete(API_ROUTES.societies.deleteDocument(societyId, docId));
    handleSuccess("Document deleted");
    return res.data;
  } catch (err) {
    handleError(err, "DELETE DOCUMENT ERROR");
  }
};

export const updateSociety = async (id, data) => {
  try {
    const res = await api.put(API_ROUTES.societies.details(id), data);
    handleSuccess("Society updated successfully");
    return res.data;
  } catch (err) {
    handleError(err, "UPDATE SOCIETY ERROR");
  }
};

export const deleteSociety = async (id) => {
  try {
    const res = await api.delete(API_ROUTES.societies.details(id));
    handleSuccess("Society deleted successfully");
    return res.data;
  } catch (err) {
    handleError(err, "DELETE SOCIETY ERROR");
  }
};

/* =====================================================
   TOWERS
===================================================== */

export const getTowersBySociety = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.towers(societyId));
    return res.data;
  } catch (err) {
    handleError(err, "GET TOWERS ERROR");
  }
};

export const createTowers = async (data) => {
  try {
    const res = await api.post(API_ROUTES.societies.towersBulkCreate, data);
    handleSuccess("Towers saved successfully");
    return res.data;
  } catch (err) {
    handleError(err, "CREATE TOWERS ERROR");
  }
};

/* =====================================================
   UNITS / CONFIG
===================================================== */

/**
 * 🔥 This now returns:
 * [
 *   {
 *     tower_id,
 *     tower_name,
 *     total_floors,
 *     units_per_floor,
 *     units: ["101","102","103"...]  <-- NEW
 *   }
 * ]
 */
export const getTowerConfigs = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.configs(societyId));
    const data = res.data;

    // Backend returns: { towers: [...], merged_units: [...] }
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.towers)) return data.towers;
    return [];
  } catch (err) {
    handleError(err, "GET CONFIG ERROR");
  }
};

/**
 * Generate units (floors + flats)
 */
export const generateUnits = async (data) => {
  try {
    const res = await api.post(API_ROUTES.societies.generateUnits, data);
    handleSuccess("Units generated successfully");
    return res.data;
  } catch (err) {
    handleError(err, "GENERATE UNITS ERROR");
  }
};

/**
 * Fetch dynamic unit types for society (defaults for property type + custom saved)
 */
export const getSocietyUnitTypes = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.unitTypes(societyId));
    return res.data;
  } catch (err) {
    handleError(err, "GET UNIT TYPES ERROR");
  }
};

/**
 * Add a custom unit type for society
 */
export const addSocietyUnitType = async (societyId, name) => {
  try {
    const res = await api.post(API_ROUTES.societies.addUnitType(societyId), { name });
    handleSuccess(`Unit type "${name}" saved`);
    return res.data;
  } catch (err) {
    handleError(err, "ADD UNIT TYPE ERROR");
  }
};

/**
 * Delete custom unit type for society
 */
export const deleteSocietyUnitType = async (societyId, unitTypeId) => {
  try {
    const res = await api.delete(API_ROUTES.societies.deleteUnitType(societyId, unitTypeId));
    handleSuccess("Unit type deleted");
    return res.data;
  } catch (err) {
    handleError(err, "DELETE UNIT TYPE ERROR");
  }
};

/* =====================================================
   OPTIONAL (DIRECT UNIT FETCH - FUTURE)
===================================================== */

export const getUnitsByTower = async (towerId) => {
  try {
    const res = await api.get(API_ROUTES.societies.unitsByTower(towerId));
    return res.data;
  } catch (err) {
    handleError(err, "GET UNITS ERROR");
  }
};

export const deleteTower = async (towerId) => {
  try {
    const res = await api.delete(API_ROUTES.societies.deleteTower(towerId));
    handleSuccess("Tower deleted successfully");
    return res.data;
  } catch (err) {
    handleError(err, "DELETE TOWER ERROR");
  }
};

export const getPendingApprovals = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.pendingApprovals(societyId));
    return res.data;
  } catch (err) {
    handleError(err, "GET PENDING APPROVALS ERROR");
    return [];
  }
};

export const approveUserRequest = async (societyId, requestId) => {
  try {
    const res = await api.put(API_ROUTES.societies.approveRequest(societyId, requestId));
    handleSuccess("Request approved successfully");
    return res.data;
  } catch (err) {
    handleError(err, "APPROVE REQUEST ERROR");
  }
};

export const rejectUserRequest = async (societyId, requestId, reason) => {
  try {
    const res = await api.put(API_ROUTES.societies.rejectRequest(societyId, requestId), { reason });
    handleSuccess("Request rejected successfully");
    return res.data;
  } catch (err) {
    handleError(err, "REJECT REQUEST ERROR");
  }
};

export const getEmployeeFormData = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.directoriesEmployeeFormData(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return { departments: [], floors: [], managers: [] };
  }
};

export const getSocietyResidents = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.directoriesResidents(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const createSocietyResident = async (societyId, data) => {
  try {
    const res = await api.post(API_ROUTES.societies.directoriesResidents(societyId), data);
    handleSuccess("Employee added successfully");
    return res.data;
  } catch (err) {
    handleError(err, "ADD EMPLOYEE ERROR");
  }
};

export const getSocietyTenants = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.directoriesTenants(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const getSocietyVehicles = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.directoriesVehicles(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const getSocietyPets = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.directoriesPets(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const createSocietyVehicle = async (societyId, data) => {
  try {
    const res = await api.post(API_ROUTES.societies.directoriesVehicles(societyId), data);
    handleSuccess("Vehicle added successfully");
    return res.data;
  } catch (err) {
    handleError(err, "ADD VEHICLE ERROR");
  }
};

export const createSocietyTenant = async (societyId, data) => {
  try {
    const res = await api.post(API_ROUTES.societies.directoriesTenants(societyId), data);
    handleSuccess("Contractor/Tenant registered successfully");
    return res.data;
  } catch (err) {
    handleError(err, "ADD CONTRACTOR/TENANT ERROR");
  }
};


export const getSocietyGates = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.gates(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const createSocietyGate = async (societyId, gateData) => {
  try {
    const res = await api.post(API_ROUTES.societies.gates(societyId), gateData);
    handleSuccess("Gate created successfully");
    return res.data;
  } catch (err) {
    handleError(err, "CREATE GATE ERROR");
  }
};

export const updateSocietyGate = async (societyId, gateId, gateData) => {
  try {
    const res = await api.put(API_ROUTES.societies.updateGate(societyId, gateId), gateData);
    handleSuccess("Gate updated successfully");
    return res.data;
  } catch (err) {
    handleError(err, "UPDATE GATE ERROR");
  }
};

export const deleteSocietyGate = async (societyId, gateId) => {
  try {
    const res = await api.delete(API_ROUTES.societies.deleteGate(societyId, gateId));
    handleSuccess(res?.data?.message || "Gate removed successfully");
    return res.data;
  } catch (err) {
    handleError(err, "DELETE GATE ERROR");
  }
};

export const assignStaffToGate = async (societyId, gateId, staffId) => {
  try {
    const res = await api.post(API_ROUTES.societies.assignGateStaff(societyId, gateId), { staffId });
    handleSuccess("Staff assigned to gate successfully");
    return res.data;
  } catch (err) {
    handleError(err, "ASSIGN STAFF ERROR");
  }
};

export const unassignStaffFromGate = async (societyId, gateId, staffId) => {
  try {
    const res = await api.post(API_ROUTES.societies.unassignGateStaff(societyId, gateId), { staffId });
    handleSuccess(res?.data?.message || "Staff unassigned from gate successfully");
    return res.data;
  } catch (err) {
    handleError(err, "UNASSIGN STAFF ERROR");
  }
};

export const getSocietyStaff = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.directoriesStaff(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const inviteSocietyStaff = async (societyId, inviteData) => {
  try {
    const res = await api.post(API_ROUTES.societies.inviteStaff(societyId), inviteData);
    handleSuccess("Staff invited successfully");
    return res.data;
  } catch (err) {
    handleError(err, "INVITE STAFF ERROR");
  }
};

export const updateSocietyStaff = async (societyId, staffId, staffData) => {
  try {
    const res = await api.put(API_ROUTES.societies.updateStaff(societyId, staffId), staffData);
    handleSuccess("Staff member updated successfully");
    return res.data;
  } catch (err) {
    handleError(err, "UPDATE STAFF ERROR");
  }
};

export const deleteSocietyStaff = async (societyId, staffId) => {
  try {
    const res = await api.delete(API_ROUTES.societies.deleteStaff(societyId, staffId));
    handleSuccess(res?.data?.message || "Staff member removed successfully");
    return res.data;
  } catch (err) {
    handleError(err, "DELETE STAFF ERROR");
  }
};

export const getSocietyCommittee = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.committee(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const assignCommitteeRole = async (societyId, assignData) => {
  try {
    const res = await api.post(API_ROUTES.societies.assignCommitteeRole(societyId), assignData);
    handleSuccess("Committee role assigned successfully");
    return res.data;
  } catch (err) {
    handleError(err, "ASSIGN ROLE ERROR");
  }
};

export const revokeCommitteeRole = async (societyId, memberId) => {
  try {
    const res = await api.post(API_ROUTES.societies.revokeCommitteeRole(societyId, memberId));
    handleSuccess("Committee role revoked successfully");
    return res.data;
  } catch (err) {
    handleError(err, "REVOKE ROLE ERROR");
  }
};

export const getSocietyConfigs = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.settings(societyId));
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const updateSocietyConfigs = async (societyId, configData) => {
  try {
    const res = await api.put(API_ROUTES.societies.settings(societyId), configData);
    handleSuccess("Society configuration updated successfully");
    return res.data;
  } catch (err) {
    handleError(err, "UPDATE CONFIGS ERROR");
  }
};

export const updateSocietyResident = async (societyId, userId, updateData) => {
  try {
    const res = await api.put(API_ROUTES.societies.updateResident(societyId, userId), updateData);
    handleSuccess("Member details updated successfully");
    return res.data;
  } catch (err) {
    handleError(err, "UPDATE RESIDENT ERROR");
  }
};

export const revokeSocietyResident = async (societyId, userId, data = {}) => {
  try {
    const res = await api.post(API_ROUTES.societies.revokeResident(societyId, userId), data);
    handleSuccess("Member access revoked successfully");
    return res.data;
  } catch (err) {
    handleError(err, "REVOKE RESIDENT ERROR");
  }
};

export const getSocietyHotlines = async (societyId) => {
  try {
    const res = await api.get(API_ROUTES.societies.directoriesHotlines(societyId));
    return res.data;
  } catch (err) {
    console.error("GET HOTLINES ERROR:", err);
    return [];
  }
};

export const saveSocietyHotline = async (societyId, hotlineData) => {
  try {
    const res = await api.post(API_ROUTES.societies.saveHotline(societyId), hotlineData);
    handleSuccess("Hotline updated successfully");
    return res.data;
  } catch (err) {
    handleError(err, "SAVE HOTLINE ERROR");
  }
};

export const deleteSocietyHotline = async (societyId, hotlineId) => {
  try {
    const res = await api.delete(API_ROUTES.societies.deleteHotline(societyId, hotlineId));
    handleSuccess("Hotline deleted successfully");
    return res.data;
  } catch (err) {
    handleError(err, "DELETE HOTLINE ERROR");
  }
};

export const exportSocietyResidents = async (societyId, propertyName = "Property") => {
  try {
    const token = localStorage.getItem("token");
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`/api${API_ROUTES.societies.directoriesResidentsExport(societyId)}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to export employees");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    const cleanProp = propertyName.replace(/[^a-zA-Z0-9_-]/g, "_");
    a.download = `${cleanProp}_Employees_${Date.now()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    handleSuccess("Employees exported successfully");
    return true;
  } catch (err) {
    handleError(err, "EXPORT EMPLOYEES ERROR");
    return false;
  }
};

export const downloadEmployeeTemplate = async () => {
  try {
    const token = localStorage.getItem("token");
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    // Download template endpoint
    const response = await fetch(`/api/societies/0/directories/residents/template`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to download template");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "Employee_Import_Template.xlsx";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return true;
  } catch (err) {
    handleError(err, "DOWNLOAD TEMPLATE ERROR");
    return false;
  }
};

export const importSocietyResidents = async (societyId, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await api.post(API_ROUTES.societies.directoriesResidentsImport(societyId), formData);
    handleSuccess(res.data?.message || "Employees imported successfully");
    return res.data;
  } catch (err) {
    handleError(err, "IMPORT EMPLOYEES ERROR");
    throw err;
  }
};
